<template>
    <div class="d-flex align-center">
        <v-autocomplete
            ref="address-map-location-input"
            v-model="point"
            :items="items"
            :placeholder="$t('search_on_map')"
            bg-color="white"
            density="compact"
            hide-no-data
            hide-selected
            hide-details
            no-filter
            clearable
            return-object
            single-line
            variant="outlined"
            class="ma-3 location"
            menu-icon=""
            item-title="place_id"
            item-value="place_id"
            @update:model-value="change"
            @update:search="onSearch"
        >
            <template v-slot:append-inner>
                <v-icon v-if="!loading">mdi-magnify</v-icon>
                <v-progress-circular v-else indeterminate size="24" color="grey" />
            </template>
            <template v-slot:item="{item, props}">
                <v-list-item v-bind="props">
                    <template v-slot:title>
                        <span v-text="item.raw.description" />
                    </template>
                </v-list-item>
            </template>
            <template v-slot:selection="{item}">
                {{ item.raw.description }}
            </template>
        </v-autocomplete>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, VModel} from 'vue-facing-decorator'
    import {HotelMapMixin} from '@/components/hotels/mixins/HotelMapMixin.vue'

    @Component
    export class AddressMapLocationFilter extends HotelMapMixin {
        @VModel() point
        @Prop({required: true}) google
        @Prop({required: true}) city

        timer = null
        items = []
        loading = false
        autocompleteService = null
        sessionToken = null
        geocoder = null
        countryCode = null

        mounted() {
            this.autocompleteService = new this.google.maps.places.AutocompleteService()
            this.sessionToken = new this.google.maps.places.AutocompleteSessionToken()
            this.geocoder = new this.google.maps.Geocoder()

            this.loadCountryCode()
        }

        onSearch(val) {
            if (this.timer) {
                clearTimeout(this.timer)
            }
            this.timer = setTimeout(() => {
                this.initSearch(val)
            }, 500)
        }

        async initSearch(val) {
            if (this.loading || (this.items.length > 0 && this.items.indexOf(val) !== -1) || !val || val.length < 2)
                return
            this.loading = true

            const location = this.getLocationFromString(val)

            if (location) {
                const {results} = await this.geocoder.geocode({location, language: this.$i18n.locale})

                if (results?.length) {
                    const item = results[0]
                    const {lat, lng} = item.geometry.location.toJSON()
                    this.items = [
                        {
                            ...item,
                            description: item.formatted_address,
                            latitude: lat,
                            longitude: lng,
                        },
                    ]
                } else {
                    this.items = []
                }

                this.loading = false
            } else {
                this.autocompleteService.getPlacePredictions(
                    {
                        input: val,
                        types: ['geocode'],
                        componentRestrictions: this.countryCode && {country: this.countryCode},
                        locationBias: {
                            center: this.cityLocation,
                            radius: 30000,
                        },
                        language: this.$i18n.locale,
                        sessionToken: this.sessionToken,
                    },
                    (results, status) => {
                        if (status === this.google.maps.places.PlacesServiceStatus.OK) {
                            this.items = results
                        }
                        this.loading = false
                    }
                )
            }
        }

        change() {
            this.$refs['address-map-location-input']?.blur()
        }

        get cityLocation() {
            return {lat: this.city.latitude, lng: this.city.longitude}
        }

        loadCountryCode() {
            this.geocoder
                .geocode({location: this.cityLocation})
                .then(({results}) => {
                    const addressComponents = results[0].address_components
                    const index = addressComponents.findIndex(item => item.types.includes('country'))

                    if (~index) {
                        this.countryCode = addressComponents[index].short_name
                    }
                })
                .catch(() => {})
        }

        getLocationFromString(val) {
            const result = val.match(
                /^(?<lat>-?([0-8]?[0-9]|90)(\.[0-9]{1,10})),\s*(?<lng>-?([0-9]{1,2}|1[0-7][0-9]|180)(\.[0-9]{1,10}))$/
            )

            if (result) {
                const {lat, lng} = result.groups
                return {lat: +lat, lng: +lng}
            }

            return null
        }
    }

    export default AddressMapLocationFilter
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    .location {
        width: 20vw;

        @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
            width: 60vw;
        }
    }

    .radius-field {
        width: 5vw;
    }

    :deep(.v-dialog) {
        overflow-y: hidden;
    }
    :deep(.v-label) {
        font-size: inherit;
    }

    :deep(.v-autocomplete__selection) {
        line-height: unset !important;
        height: unset !important;
        display: flex;
        align-items: start;
    }
</style>
